import { Injectable, Signal, computed, effect, inject, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
  CallState,
  CallSession,
  CallDisposition,
  CallError,
  CallDirection
} from './call-types';
import { PostCallActionModalComponent } from '../../profile/company/call-configuration.component/dialog-call/post-call-action-modal.component';

// Service imports
import { CallStateStore } from './call-state.store';
import { CallLifecycleService } from './call-lifecycle.service';
import { CallSessionService } from './call-session.service';
import { CallEventsService, CallStateChange, CallEndEvent } from './call-events.service';
import { CallTimerService } from './call-timer.service';
import { CallApiService, CallRecord, DispositionUpdate } from './call-api.service';
import { TelnyxIntegrationService } from './telnyx-integration.service';
import { CallErrorHandler, UserFriendlyError } from './call-error-handler.service';
import { PhoneUtil } from '../../shared/utils/phone.util';
import { authService } from '../auth.service';
import { GlobalCallContextService } from '../global-call-context.service';
import { GlobalCallPanelService } from '../global-call-panel.service';

/**
 * CallFacadeService
 *
 * Main entry point and coordination layer for the call management system.
 * Provides a simple, unified public API for components to interact with the call system.
 *
 * Architecture:
 * - Delegates to specialized services (no duplicate logic)
 * - Provides reactive state through signals and observables
 * - Handles initialization and cleanup
 * - Coordinates between WebRTC, Socket, and API integrations
 * - Implements error handling and recovery
 *
 * Target: ~250-350 lines (can be up to 500 if needed)
 */

/**
 * Call context information
 */
export interface CallContext {
  sessionId: string | null;
  leadId: number | null;
  phoneNumber: string | null;
  direction: CallDirection | null;
  state: CallState;
  duration: number;
  startTime: Date | null;
  canMakeCall: boolean;
  isInCall: boolean;
}

/**
 * Call disposition payload for setting disposition
 */
export interface CallDispositionPayload {
  disposition: CallDisposition;
  notes?: string;
  scheduledFollowUp?: Date;
  outcome?: string;
  tags?: string[];
}

/**
 * Call history record
 */
export interface CallHistoryRecord extends CallSession {
  formattedDuration?: string;
  formattedDate?: string;
  displayPhoneNumber?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CallFacadeService implements OnDestroy {
  // ============================================================================
  // Dependencies (injected services)
  // ============================================================================

  private store = inject(CallStateStore);
  private lifecycle = inject(CallLifecycleService);
  private sessions = inject(CallSessionService);
  private events = inject(CallEventsService);
  private timers = inject(CallTimerService);
  private api = inject(CallApiService);
  private telnyx = inject(TelnyxIntegrationService);
  private errorHandler = inject(CallErrorHandler);
  private authService = inject(authService);
  private dialog = inject(MatDialog);
  private globalCallContext = inject(GlobalCallContextService);
  private globalCallPanel = inject(GlobalCallPanelService);

  // Cleanup subject
  private destroy$ = new Subject<void>();

  // ============================================================================
  // Reactive State (signals from CallStateStore)
  // ============================================================================

  /** Current call state */
  readonly currentState: Signal<CallState> = this.store.callState;

  /** Active call session */
  readonly activeCall: Signal<CallSession | null> = this.store.activeCall;

  /** Call history */
  readonly callHistory: Signal<readonly CallSession[]> = this.sessions.callHistory;

  /** Is call active (any non-idle state) */
  readonly isCallActive: Signal<boolean> = this.store.isCallActive;

  /** Call duration in seconds */
  readonly callDuration: Signal<number> = this.store.callDuration;

  /** Last error */
  readonly lastError: Signal<string | null> = this.store.lastError;

  /** Is processing (operation in progress) */
  readonly isProcessing: Signal<boolean> = this.store.isProcessing;

  /** Can make call (connected and idle) */
  readonly canMakeCall: Signal<boolean> = this.store.canMakeCall;

  /** Is in call (dialing, ringing, connecting, connected, held) */
  readonly isInCall: Signal<boolean> = this.store.isInCall;

  /** WebRTC connection status */
  readonly isConnected: Signal<boolean> = this.store.isConnected;

  /** Can end call */
  readonly canEndCall: Signal<boolean> = this.store.canEndCall;

  // ============================================================================
  // Observable Streams (from CallEventsService)
  // ============================================================================

  /** State change events */
  readonly stateChanges$: Observable<CallStateChange> = this.events.onStateChange();

  /** Call started events */
  readonly callStarted$: Observable<CallSession> = this.events.onCallStarted();

  /** Call ended events */
  readonly callEnded$: Observable<CallEndEvent> = this.events.onCallEnded();

  /** Call error events */
  readonly callErrors$: Observable<CallError> = this.events.onError();

  // ============================================================================
  // Initialization
  // ============================================================================

  constructor() {
    // Initialize integrations
    this.initializeIntegrations();

    // Setup reactive connection monitoring
    this.setupConnectionMonitoring();

    // Subscribe to call ended events to show disposition modal for outbound calls
    this.setupCallEndedSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanup();
  }

  /**
   * Setup subscription to handle call ended events
   * Shows disposition modal for outbound answered calls
   */
  private setupCallEndedSubscription(): void {
    this.callEnded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CallEndEvent) => {
        const { session, disposition } = event;

        console.log('📞 [CallFacadeService] Call ended event received', {
          direction: session.direction,
          disposition: session.disposition,
          backendCallId: session.backendCallId
        });

        // Show disposition modal for answered calls (both inbound and outbound)
        const wasAnswered = session.disposition === 'answered' || disposition === 'answered';

        if (wasAnswered && session.backendCallId) {
          console.log(`📞 [CallFacadeService] Showing disposition modal for ${session.direction} call`);
          // Small delay to ensure UI is ready
          setTimeout(() => {
            this.showDispositionModal(session);
          }, 500);
        }
      });
  }

  /**
   * Handle socket hangup event for showing disposition modal
   * This is triggered when the backend confirms the call has ended
   */
  private handleSocketHangupForDisposition(data: any): void {
    const backendCallId = data.call_id || data.callId;
    const hangupDetails = data.hangup_details || {};

    // Determine if this was an answered call based on hangup cause
    const cause = hangupDetails.cause || '';
    const unansweredCauses = ['user_busy', 'busy', 'no_answer', 'timeout', 'rejected', 'cancelled', 'originator_cancel'];
    const wasNotAnsweredByCause = unansweredCauses.includes(cause);

    // Get the last known session info (might be cleared by now)
    const lastSession = this.store.getActiveCall();
    const callHistory = this.callHistory();
    const recentSession = lastSession || (callHistory.length > 0 ? callHistory[0] : null);

    console.log('📞 [CallFacadeService] Processing socket hangup for disposition', {
      backendCallId,
      cause,
      wasNotAnsweredByCause,
      hasSession: !!recentSession,
      sessionDirection: recentSession?.direction,
      sessionDisposition: recentSession?.disposition,
      sessionState: recentSession?.state
    });

    // Check if call was actually answered
    // A call is answered if:
    // 1. The session disposition is 'answered', OR
    // 2. The hangup cause is NOT one of the unanswered causes AND the session was connected
    const wasAnswered = recentSession?.disposition === 'answered' ||
                        (!wasNotAnsweredByCause && recentSession?.state === 'connected');

    // Show disposition for answered calls (both inbound and outbound)
    if (wasAnswered && backendCallId) {
      console.log(`📞 [CallFacadeService] Showing disposition modal for ${recentSession?.direction} answered call`, {
        backendCallId,
        phoneNumber: recentSession?.phoneNumber,
        leadId: recentSession?.leadId
      });

      // Create a session-like object with the data we need
      const sessionForModal: CallSession = {
        ...recentSession!,
        backendCallId: backendCallId
      };

      // Small delay to ensure UI is ready
      setTimeout(() => {
        this.showDispositionModal(sessionForModal);
      }, 500);
    } else {
      console.log('📞 [CallFacadeService] Skipping disposition modal', {
        reason: !recentSession ? 'no session' :
                !wasAnswered ? 'not answered' :
                !backendCallId ? 'no backendCallId' : 'unknown'
      });
    }
  }

  /**
   * Initialize all integrations
   */
  private initializeIntegrations(): void {
    try {
      // Initialize Telnyx WebRTC integration
      this.telnyx.initialize();

      // Sync connection state from Telnyx (initial sync)
      this.store.setConnected(this.telnyx.connected());


    } catch (error) {
      console.error('❌ CallFacadeService: Initialization failed', error);
      const processedError = this.errorHandler.processError(error, 'INITIALIZATION_FAILED');
      this.events.emitError(processedError);
    }
  }

  /**
   * Setup reactive connection monitoring
   * Continuously syncs Telnyx connection state with store
   */
  private setupConnectionMonitoring(): void {
    effect(() => {
      // Read the Telnyx connection signal
      const isConnected = this.telnyx.connected();

      // Sync to store
      this.store.setConnected(isConnected);

      // Log connection state changes

    }, { allowSignalWrites: true });
  }

  /**
   * Cleanup on service destruction
   */
  private cleanup(): void {
    try {
      this.telnyx.cleanup();
      this.timers.clearAll();

    } catch (error) {
      console.error('❌ CallFacadeService: Cleanup error', error);
    }
  }

  // ============================================================================
  // Call Operations
  // ============================================================================

  /**
   * Initiate an outbound call
   *
   * @param phoneNumber - Phone number to call
   * @param leadId - Optional lead ID
   * @param leadName - Optional lead name for display in call panel
   * @returns Promise<boolean> - true if call initiated successfully
   */
  async initiateCall(phoneNumber: string, leadId?: number, leadName?: string): Promise<boolean> {
    try {
      // Validate phone number
      const formattedPhone = PhoneUtil.validateAndFormat(phoneNumber);
      if (!formattedPhone) {
        const error = this.errorHandler.processError(
          new Error('Invalid phone number format'),
          'INVALID_PHONE_NUMBER'
        );
        this.events.emitError(error);
        this.store.setError(error.message);
        return false;
      }

      // Check if can make call
      if (!this.canMakeCall()) {
        let reason: string;
        if (!this.isConnected()) {
          reason = 'WebRTC not connected. Please refresh the page or check your network connection.';
        } else if (this.currentState() !== 'idle') {
          reason = `Cannot make call from state: ${this.currentState()}`;
        } else if (this.isProcessing()) {
          reason = 'Another call operation is in progress';
        } else {
          reason = 'Cannot make call at this time';
        }
        const error = this.errorHandler.processError(
          new Error(reason),
          !this.isConnected() ? 'WEBRTC_NOT_CONNECTED' : 'INVALID_STATE_TRANSITION'
        );
        this.events.emitError(error);
        this.store.setError(error.message);
        return false;
      }

      // Initialize call session through lifecycle
      const session = await this.lifecycle.initializeOutbound(formattedPhone, leadId);

      // Make call through Telnyx integration
      const telnyxCall = await this.telnyx.makeCall(formattedPhone, leadId);

      // Update session with backend call ID and Telnyx call ID from response
      const backendCallId = (telnyxCall as any)?.backendCallId;
      const telnyxCallId = (telnyxCall as any)?.id;

      if (telnyxCallId) {
        this.sessions.updateSession(session.id, { telnyxCallId });
        session.telnyxCallId = telnyxCallId;
      }

      if (backendCallId) {
        this.sessions.updateSession(session.id, { backendCallId });
        session.backendCallId = backendCallId;
        console.log('📞 [CallFacadeService] Session updated with backendCallId:', backendCallId);
        // Webhook links telnyx_call_control_id via X-Backend-Call-Id custom header
      }

      // Update the store with the session containing the backendCallId
      this.store.setActiveCall(session);

      // Capture call context for outbound call with lead info if available
      this.globalCallContext.captureOutboundContext(session, leadName ? {
        id: leadId!,
        name: leadName,
        phone: formattedPhone
      } : undefined);

      // Clear any previous errors
      this.store.setError(null);

      return true;

    } catch (error) {
      console.error('❌ Call initiation failed', error);
      const processedError = this.errorHandler.processError(error, 'CALL_INITIATION_FAILED');
      this.events.emitError(processedError);
      this.store.setError(processedError.message);
      return false;
    }
  }

  /**
   * Answer an incoming call
   *
   * @returns Promise<boolean> - true if answer succeeded
   */
  async answerCall(): Promise<boolean> {
    try {
      const currentSession = this.store.getActiveCall();
      if (!currentSession) {
        throw new Error('No active call to answer');
      }

      if (currentSession.direction !== 'inbound') {
        throw new Error('Can only answer inbound calls');
      }

      // Answer through Telnyx integration
      const success = this.telnyx.answerCall();
      if (!success) {
        throw new Error('Failed to answer call - Telnyx returned false');
      }

      // Clear any previous errors
      this.store.setError(null);


      return true;

    } catch (error) {
      console.error('❌ Answer call failed', error);
      const processedError = this.errorHandler.processError(error, 'ANSWER_FAILED');
      this.events.emitError(processedError);
      this.store.setError(processedError.message);
      return false;
    }
  }

  /**
   * End the current call
   *
   * @param disposition - Optional call disposition
   * @param notes - Optional call notes
   * @returns Promise<boolean> - true if call ended successfully
   */
  async endCall(disposition?: CallDisposition, notes?: string): Promise<boolean> {
    try {
      const currentSession = this.store.getActiveCall();
      if (!currentSession) {
        console.warn('⚠️ No active call to end');
        return false;
      }

      // Update session with disposition/notes if provided
      if (disposition) {
        this.sessions.updateSession(currentSession.id, { disposition });
      }
      if (notes) {
        const existingNotes = currentSession.notes || '';
        this.sessions.updateSession(currentSession.id, {
          notes: existingNotes ? `${existingNotes}\n${notes}` : notes
        });
      }

      // Hangup through Telnyx
      this.telnyx.hangupCall();

      // Terminate call through lifecycle
      const finalDisposition = disposition || currentSession.disposition || 'answered';
      this.lifecycle.terminateCall(currentSession, finalDisposition);

      // Save call record to backend (non-blocking)
      this.saveCallRecord(currentSession).catch(err => {
        console.warn('⚠️ Failed to save call record', err);
      });

      // Clear any previous errors
      this.store.setError(null);

      // Note: Disposition modal is triggered via callEnded$ subscription
      // in setupCallEndedSubscription() for all outbound answered calls

      return true;

    } catch (error) {
      console.error('❌ End call failed', error);
      const processedError = this.errorHandler.processError(error, 'CALL_END_FAILED');
      this.events.emitError(processedError);
      this.store.setError(processedError.message);
      return false;
    }
  }

  /**
   * Cancel call (during ringing/dialing)
   *
   * @returns Promise<boolean> - true if call cancelled successfully
   */
  async cancelCall(): Promise<boolean> {
    try {
      const currentSession = this.store.getActiveCall();
      if (!currentSession) {
        return false;
      }

      const currentState = this.currentState();
      if (!['dialing', 'ringing', 'connecting'].includes(currentState)) {
        console.warn('⚠️ Can only cancel calls in dialing/ringing/connecting states');
        return false;
      }

      // Update disposition
      this.sessions.updateSession(currentSession.id, { disposition: 'cancelled' });

      // Hangup through Telnyx
      this.telnyx.hangupCall();

      // Terminate call
      this.lifecycle.terminateCall(currentSession, 'cancelled');


      return true;

    } catch (error) {
      console.error('❌ Cancel call failed', error);
      const processedError = this.errorHandler.processError(error, 'CANCEL_FAILED');
      this.events.emitError(processedError);
      return false;
    }
  }

  /**
   * Make another call to the same number
   *
   * @returns Promise<boolean> - true if call initiated successfully
   */
  async makeAnotherCall(): Promise<boolean> {
    try {
      const lastCall = this.store.getActiveCall() || this.getLastCallFromHistory();
      if (!lastCall) {
        throw new Error('No previous call to redial');
      }

      return this.initiateCall(lastCall.phoneNumber, lastCall.leadId);

    } catch (error) {
      console.error('❌ Make another call failed', error);
      const processedError = this.errorHandler.processError(error, 'REDIAL_FAILED');
      this.events.emitError(processedError);
      return false;
    }
  }

  /**
   * Toggle hold state
   *
   * @returns boolean - true if toggle succeeded
   */
  toggleHold(): boolean {
    try {
      const currentSession = this.store.getActiveCall();
      if (!currentSession) {
        return false;
      }

      const currentState = this.currentState();
      if (currentState !== 'connected' && currentState !== 'held') {
        console.warn('⚠️ Can only toggle hold in connected/held states');
        return false;
      }

      // Toggle through Telnyx
      this.telnyx.toggleHold();


      return true;

    } catch (error) {
      console.error('❌ Toggle hold failed', error);
      const processedError = this.errorHandler.processError(error, 'HOLD_TOGGLE_FAILED');
      this.events.emitError(processedError);
      return false;
    }
  }

  // ============================================================================
  // Disposition
  // ============================================================================

  /**
   * Set call disposition
   *
   * @param payload - Disposition payload
   * @returns Promise<boolean> - true if disposition set successfully
   */
  async setCallDisposition(payload: CallDispositionPayload): Promise<boolean> {
    try {
      const currentSession = this.store.getActiveCall();
      if (!currentSession || !currentSession.backendCallId) {
        throw new Error('No active call or backend call ID missing');
      }

      // Update session locally
      this.sessions.updateSession(currentSession.id, {
        disposition: payload.disposition,
        notes: payload.notes
      });

      // Update backend
      const dispositionUpdate: DispositionUpdate = {
        disposition: payload.disposition,
        notes: payload.notes,
        scheduled_follow_up: payload.scheduledFollowUp,
        outcome: payload.outcome,
        tags: payload.tags
      };

      await this.api.updateDisposition(currentSession.backendCallId as number, dispositionUpdate).toPromise();

      // Emit disposition update event
      this.events.emitDispositionUpdate(
        currentSession.id,
        payload.disposition,
        payload.notes
      );


      return true;

    } catch (error) {
      console.error('❌ Set disposition failed', error);
      const processedError = this.errorHandler.processError(error, 'DISPOSITION_UPDATE_FAILED');
      this.events.emitError(processedError);
      return false;
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /**
   * Force reset to idle state
   * Use with caution - clears all state
   */
  forceResetToIdle(): void {
    try {
      // Hangup any active WebRTC call
      this.telnyx.hangupCall();

      // Clear all timers
      this.timers.clearAll();

      // Reset store state
      this.store.reset();

      // Clear sessions
      this.sessions.clearAll();


    } catch (error) {
      console.error('❌ Force reset failed', error);
    }
  }

  /**
   * Get current call context
   *
   * @returns CallContext - Current call context information
   */
  getCurrentCallContext(): CallContext {
    const session = this.store.getActiveCall();
    return {
      sessionId: session?.id || null,
      leadId: session?.leadId || null,
      phoneNumber: session?.phoneNumber || null,
      direction: session?.direction || null,
      state: this.currentState(),
      duration: this.callDuration(),
      startTime: session?.startTime || null,
      canMakeCall: this.canMakeCall(),
      isInCall: this.isInCall()
    };
  }

  /**
   * Get lead call history
   *
   * @param leadId - Lead ID
   * @param limit - Maximum number of records
   * @returns Observable with call history
   */
  getLeadCallHistory(leadId: number, limit: number = 50): Observable<CallHistoryRecord[]> {
    return this.api.getLeadCallHistory(leadId, limit);
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Save call record to backend (non-blocking)
   */
  private async saveCallRecord(session: CallSession): Promise<void> {
    try {
      const user = this.authService.getCurrentUser();
      const callRecord = this.api.buildCallRecord(
        session,
        user?.user_id,
        user?.company_id
      );

      await this.api.createCall(callRecord).toPromise();

    } catch (error) {
      console.error('❌ Save call record failed', error);
      const processedError = this.errorHandler.processError(error, 'BACKEND_SAVE_FAILED');
      this.events.emitError(processedError);
    }
  }

  /**
   * Get last call from history
   */
  private getLastCallFromHistory(): CallSession | null {
    const history = this.callHistory();
    return history.length > 0 ? history[0] : null;
  }

  /**
   * Show post-call disposition modal for answered calls (both inbound and outbound)
   */
  private showDispositionModal(session: CallSession): void {
    try {
      const callId = session.backendCallId;

      if (!callId) {
        console.warn('⚠️ Cannot show disposition modal: no backend call ID available');
        return;
      }

      // Calculate duration in milliseconds
      const duration = session.duration ||
        (session.endTime && session.startTime
          ? new Date(session.endTime).getTime() - new Date(session.startTime).getTime()
          : 0);

      console.log(`📞 [CallFacadeService] Opening disposition modal for ${session.direction} call`, {
        callId,
        phoneNumber: session.phoneNumber,
        duration,
        leadId: session.leadId
      });

      this.dialog.open(PostCallActionModalComponent, {
        width: '500px',
        maxWidth: '95vw',
        disableClose: true,
        data: {
          call: session,
          leadId: session.leadId || null,
          phoneNumber: session.phoneNumber || 'Unknown',
          duration: duration,
          callId: callId
        },
        panelClass: 'post-call-dialog'
      });

    } catch (error) {
      console.error('❌ Failed to show disposition modal:', error);
    }
  }
}
